/**
 * A piece that loops while it is on screen. Its run starts when the root comes on screen and is
 * cancelled when it leaves, so nothing ticks off screen; the root reads `data-motion-state`
 * `running` or `idle` for the parts that show only while the piece runs; a root with the state is
 * initialized. A root that leaves the document (a content swap) releases its observer: a watched
 * node is a retained node.
 *
 * A hidden page (another tab, the back/forward cache) also cancels the run: its clock keeps going
 * without frames, and a run left in flight jumps on return, the compositor ahead of layout.
 */
import {createRun} from './run.js';

/**
 * @typedef {object} Play
 * @property {() => void} stop End the run and stop watching the root: the piece is idle for good.
 */

/**
 * @param {HTMLElement} root
 * @param {(run: import('./run.js').Run) => void} start Adds every animation of one loop to the run.
 * @returns {Play}
 */
export function playOnScreen(root, start) {
    /** @type {import('./run.js').Run|null} */
    let run = null;
    let isOnScreen = false;
    const released = new AbortController();
    root.dataset.motionState = 'idle';
    const update = () => {
        run?.cancel();
        run = null;
        if (!root.isConnected) {
            release();
            return;
        }
        if (isOnScreen && document.visibilityState === 'visible') {
            run = createRun();
            start(run);
        }
        root.dataset.motionState = run ? 'running' : 'idle';
    };
    const observer = new IntersectionObserver(([entry]) => {
        isOnScreen = entry.isIntersecting;
        update();
    });
    const release = () => {
        observer.disconnect();
        released.abort();
    };
    observer.observe(root);
    document.addEventListener('visibilitychange', update, {signal: released.signal});

    return {
        stop() {
            release();
            run?.cancel();
            run = null;
            root.dataset.motionState = 'idle';
        },
    };
}

/**
 * Hold the CSS loops (infinite animations) inside a root while it is off screen: a loop costs
 * main-thread work on every frame, seen or not (56 off-screen play-button pulses on /reviews took
 * about 60 ms of every second). A root that leaves the document releases its observer.
 * @param {Element} root
 * @param {Element} [watched] The box whose visibility decides. A part that moves on the compositor
 * (a marquee card) is not seen moving by an observer: watch a box that stays put.
 */
export function holdLoopsOffScreen(root, watched = root) {
    const observer = new IntersectionObserver((entries) => {
        if (!root.isConnected) {
            observer.disconnect();
            return;
        }
        const isOnScreen = entries.at(-1).isIntersecting;
        for (const animation of root.getAnimations({subtree: true})) {
            if (animation.effect?.getComputedTiming().iterations === Infinity) {
                if (isOnScreen) {
                    animation.play();
                } else {
                    animation.pause();
                }
            }
        }
    });
    observer.observe(watched);
}
